import { Inject, Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { IDataAuth } from '../../interfaces/data-auth.interface';
import { JwtHelperService } from '@auth0/angular-jwt';
import { IUser } from '../../interfaces/auth.interface';
import { DOCUMENT } from '@angular/common';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
const jwt = new JwtHelperService();

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpHeaders = new HttpHeaders({
    Authorization: `Bearer ${this.getToken()}`,
  });
  private socket$!: WebSocketSubject<any>;
  private socketInstance = false;
  private validClose = false;
  private isSocketOpen = false;
  private messagesSubject$ = new Subject<WebSocketSubject<any>>();
  public messages$ = this.messagesSubject$.pipe(
    switchMap((value: any) => {
      return value;
    })
  );
  urlRedirectText = '';
  constructor(
    private cookieService: CookieService,
    private http: HttpClient,
    private router: Router,
    @Inject(DOCUMENT) document: any
  ) {}

  /**
   * Get Token
   *
   * @return {string}
   */
  getToken(): string {
    return this.cookieService.get('auth_key');
  }

  redirectLogin(url: string) {
    this.urlRedirectText = url;
  }

  getUrlRedirectLogin(): string {
    return this.urlRedirectText;
  }

  /**
   * Get Refresh Token
   *
   * @return {string}
   */
  getRefreshToken(): string {
    return this.cookieService.get('auth_refesh');
  }

  getDataToken(): IUser {
    const token = this.cookieService.get('auth_key');
    return jwt.decodeToken(token) as IUser;
  }
  /**
   * Set Token
   *
   * {string} token
   */
  setToken(token: string, refreshToken: string = '') {
    const domain = this.getDomain();
    this.cookieService.set('auth_key', token, 24, '/', domain.domain);
    this.cookieService.set('auth_refesh', refreshToken, 24, '/', domain.domain);

    this.cookieService.set(
      'authTimeOut',
      new Date().getTime().toString(),
      24,
      '/',
      domain.domain
    );
  }

  emitDataWs(url: string, channel: string, data: any) {
    // Normalizar la URL: remover protocolo si existe y agregar wss://
    let normalizedUrl = url.trim();

    // Remover protocolos existentes (wss://, ws://, https://, http://)
    normalizedUrl = normalizedUrl.replace(/^(wss?|https?):\/\//i, '');
    // Construir la URL completa con wss://
    const urlChannel = `wss://${normalizedUrl}?channel=${channel}`;

    // Resetear el flag de conexión abierta
    this.isSocketOpen = false;

    // Conectar y esperar a que esté lista antes de enviar
    this.connect(urlChannel);

    // Intentar enviar cuando la conexión esté abierta
    const maxAttempts = 10;
    let attempts = 0;

    const trySend = () => {
      attempts++;

      if (this.isSocketOpen && this.socket$ && !this.socket$.closed) {
        try {
          this.socket$.next(data);

          // Esperar un poco más para asegurar que el mensaje se envió
          setTimeout(() => {
            this.close();
          }, 500);
        } catch (error) {
          this.close();
        }
      } else if (attempts < maxAttempts) {
        // Intentar de nuevo en 100ms
        setTimeout(trySend, 100);
      } else {
        console.error(
          '[WebSocket] ❌ Timeout: La conexión no se estableció a tiempo'
        );
        this.close();
      }
    };

    // Iniciar intentos después de un breve delay
    setTimeout(trySend, 100);
  }

  public connect(WS_ENDPOINT: string): void {
    if (!this.socketInstance) {
      this.socket$ = this.getNewWebSocket(WS_ENDPOINT);
      this.messagesSubject$.next(this.socket$);
      this.socketInstance = true;
    }
  }

  private getNewWebSocket(WS_ENDPOINT: string): WebSocketSubject<any> {
    const socket = webSocket({
      url: WS_ENDPOINT,
      openObserver: {
        next: () => {
          this.isSocketOpen = true;
        },
      },
      closeObserver: {
        next: (event: CloseEvent) => {
          if (!this.validClose) {
            this.socketInstance = false;
          } else {
            this.socketInstance = false;
            this.validClose = false;
          }
        },
      },
    });

    // Suscribirse para capturar mensajes y errores
    socket.subscribe({
      next: (message) => {
      },
      error: (error) => {
        this.socketInstance = false;
      },
      complete: () => {
      },
    });

    return socket;
  }

  close() {
    if (this.socket$ && !this.socket$.closed) {
      this.validClose = true;
      this.socket$.complete();
      this.socketInstance = false;
    }
  }

  getDomain() {
    const host = document.location.hostname;
    const firstDotIndex = host.indexOf('.');
    const domain = host.substring(firstDotIndex);
    return {
      domain: domain.split('/')[0],
    };
  }

  countChart(cadena: string, search: string) {
    let indices = [];
    for (let i = 0; i < cadena.length; i++) {
      if (cadena[i].toLowerCase() === search) indices.push(i);
    }
    return indices;
  }

  /**
   * Remove Token
   */
  removeToken() {
    this.cookieService.delete('auth_key');
    this.cookieService.delete('auth_refresh');
    this.cookieService.delete('authTimeOut');
  }

  /**
   * Logout
   */
  logout(): void {
    this.removeToken();
    this.router.navigate(['/login']);
  }

  login(auth: IDataAuth, url: string) {
    return this.http.post(`${url}/signin`, auth);
  }

  forgotPassword(url: string, username: string) {
    return this.http.post(`${url}/password/forgot`, {
      username: username.trim(),
    });
  }

  resetPassword(url: string, newPassword: string, username: string) {
    return this.http.post(`${url}/password/modification`, {
      newPassword: newPassword.trim(),
      username,
    });
  }

  verifySms(url: string, pk: string, sk: string, codeSMS: string) {
    return this.http.post(`${url}/users/${pk}/${sk}/sms/validation`, {
      codeSMS,
    });
  }

  verifyTotp(
    url: string,
    pk: string,
    sk: string,
    token: string,
    base32Secret: string
  ) {
    return this.http.post(`${url}/users/${pk}/${sk}/totp/validation`, {
      token,
      base32Secret,
    });
  }
}
